import { useEffect, useState } from "react";
import type { MeResponse, ProjectDetail } from "@handvero/shared";
import { api, errorText } from "../api";
import { Icon, Message, Sheet } from "./ui";

/** „Wer sieht das Projekt?“: Inhaber und Admins geben ein Projekt für alle oder nur für ausgewählte Mitarbeiter frei. */
export function ProjectAccessSheet({ project, onClose, onSaved }: { project: ProjectDetail; onClose: () => void; onSaved: (p: ProjectDetail) => void }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [restricted, setRestricted] = useState(project.restricted);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(project.memberIds));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<MeResponse>("/api/me")
      .then(setMe)
      .catch((err) => setError(errorText(err)));
  }, []);

  const employees = me?.members.filter((m) => m.role === "employee") ?? [];
  const managers = me?.members.filter((m) => m.role !== "employee") ?? [];

  function toggle(id: string) {
    setChosen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError("");
    try {
      onSaved(await api.put<ProjectDetail>(`/api/projects/${project.id}/access`, { restricted, memberIds: [...chosen] }));
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Wer sieht das Projekt?" onClose={onClose}>
      <div className="access">
        <div className="access-modes" role="radiogroup" aria-label="Sichtbarkeit">
          <button type="button" role="radio" aria-checked={!restricted} className={!restricted ? "on" : undefined} onClick={() => setRestricted(false)}>
            <Icon name="user" />
            <span>
              <b>Alle in der Firma</b>
              <small>Jeder Mitarbeiter sieht Ordner, Fotos, Notizen und Termine.</small>
            </span>
          </button>
          <button type="button" role="radio" aria-checked={restricted} className={restricted ? "on" : undefined} onClick={() => setRestricted(true)}>
            <Icon name="lock" />
            <span>
              <b>Nur ausgewählte Mitarbeiter</b>
              <small>Für alle anderen ist das Projekt unsichtbar, auch in der Suche.</small>
            </span>
          </button>
        </div>

        {restricted && (
          <>
            <h4 className="access-h">Mitarbeiter</h4>
            {!me && !error && <div className="skel" role="status" aria-label="Wird geladen"><i /><i /></div>}
            {me && employees.length === 0 && (
              <p className="muted-s">Noch keine Mitarbeiter eingeladen. Das geht im Kundenbereich unter „Mitarbeiter“.</p>
            )}
            <ul className="access-list">
              {employees.map((m) => (
                <li key={m.id}>
                  <label>
                    <input type="checkbox" checked={chosen.has(m.id)} onChange={() => toggle(m.id)} />
                    <span>
                      <b>{m.name}</b>
                      <small>{m.email}</small>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            {managers.length > 0 && (
              <p className="muted-s">
                Immer dabei: {managers.map((m) => m.name).join(", ")} (Inhaber und Admins sehen alle Projekte).
              </p>
            )}
          </>
        )}
        <Message>{error}</Message>
        <button className="app-btn" disabled={busy || !me} onClick={() => void save()}>
          Speichern
        </button>
      </div>
    </Sheet>
  );
}
